import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Finstral Switch.
 *
 * Mirrors the reference implementation at `elements/toggle/toggle.twig`: a native
 * checkbox carrying `role="switch"`, laid over a decorative track. The reference
 * renders no label of its own — callers place their own text beside it — so this
 * component projects nothing either.
 */
@Component({
  selector: 'fi-switch',
  templateUrl: './switch.component.html',
  styleUrls: ['./switch.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
  ],
})
export class SwitchComponent implements ControlValueAccessor {
  @Input() checked = false;
  @Input() disabled = false;
  @Input() required = false;

  /** Accessible name, for the common case where the visible text is a sibling. */
  @Input() ariaLabel?: string;

  /** Id of the element that labels this switch, when one exists. */
  @Input() ariaLabelledby?: string;

  @Output() checkedChange = new EventEmitter<boolean>();

  private onChangeFn: ( value: boolean ) => void = () => {};
  private onTouchedFn: () => void = () => {};

  onInputChange( event: Event ): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.checked = checked;
    this.checkedChange.emit(checked);
    this.onChangeFn(checked);
    this.onTouchedFn();
  }

  writeValue( value: boolean ): void {
    this.checked = !!value;
  }

  registerOnChange( fn: ( value: boolean ) => void ): void {
    this.onChangeFn = fn;
  }

  registerOnTouched( fn: () => void ): void {
    this.onTouchedFn = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.disabled = isDisabled;
  }
}
